import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Sparkles, Lock, Volume2, VolumeX, Music, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHAKRAS = [
  { name: 'Root', sanskrit: 'Muladhara', frequency: 396, color: '#E74C3C', glow: 'rgba(231,76,60,0.4)', note: 'Ut', element: 'Earth',
    description: 'Liberation from fear & guilt', crystal: 'Red Jasper', bodyArea: 'Base of Spine', affirmation: 'I am safe. I am grounded. I belong.' },
  { name: 'Sacral', sanskrit: 'Svadhisthana', frequency: 417, color: '#F39C12', glow: 'rgba(243,156,18,0.4)', note: 'Re', element: 'Water',
    description: 'Facilitating change & creativity', crystal: 'Carnelian', bodyArea: 'Lower Abdomen', affirmation: 'I feel. I create. I flow with life.' },
  { name: 'Solar Plexus', sanskrit: 'Manipura', frequency: 528, color: '#F1C40F', glow: 'rgba(241,196,15,0.4)', note: 'Mi', element: 'Fire',
    description: 'Transformation & miracles', crystal: 'Citrine', bodyArea: 'Upper Abdomen', affirmation: 'I do. I am powerful. I am confident.' },
  { name: 'Heart', sanskrit: 'Anahata', frequency: 639, color: '#2ECC71', glow: 'rgba(46,204,113,0.4)', note: 'Fa', element: 'Air',
    description: 'Connecting & relationships', crystal: 'Rose Quartz', bodyArea: 'Center of Chest', affirmation: 'I love. I am compassion. I forgive.' },
  { name: 'Throat', sanskrit: 'Vishuddha', frequency: 741, color: '#3498DB', glow: 'rgba(52,152,219,0.4)', note: 'Sol', element: 'Ether',
    description: 'Awakening intuition & expression', crystal: 'Blue Lace Agate', bodyArea: 'Throat', affirmation: 'I speak. I express my truth freely.' },
  { name: 'Third Eye', sanskrit: 'Ajna', frequency: 852, color: '#6C5CE7', glow: 'rgba(108,92,231,0.4)', note: 'La', element: 'Light',
    description: 'Returning to spiritual order', crystal: 'Amethyst', bodyArea: 'Between Eyebrows', affirmation: 'I see. I trust my inner wisdom.' },
  { name: 'Crown', sanskrit: 'Sahasrara', frequency: 963, color: '#9B59B6', glow: 'rgba(155,89,182,0.4)', note: 'Si', element: 'Thought',
    description: 'Divine consciousness & oneness', crystal: 'Clear Quartz', bodyArea: 'Top of Head', affirmation: 'I know. I am connected to all that is.' },
];

const ChakrasPage = () => {
  usePageTitle('Kholey Frequency Lab\u2122 - Chakra Soundscapes');
  const [activeChakra, setActiveChakra] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [freePlays, setFreePlays] = useState(3);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [purchaseSubmitted, setPurchaseSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '' });
  const audioCtxRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainRef = useRef(null);

  const stopSound = useCallback(() => {
    if (gainRef.current) {
      gainRef.current.gain.exponentialRampToValueAtTime(0.0001, audioCtxRef.current.currentTime + 0.5);
      setTimeout(() => {
        if (oscillatorRef.current) {
          try { oscillatorRef.current.stop(); } catch (e) { /* already stopped */ }
          oscillatorRef.current = null;
        }
      }, 600);
    }
    setIsPlaying(false);
  }, []);

  const playFrequency = useCallback((freq) => {
    if (isPlaying) { stopSound(); return; }
    if (freePlays <= 0 && !purchaseSubmitted) { setShowPaywall(true); return; }

    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);

    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq + 2, ctx.currentTime);
    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(0.08, ctx.currentTime);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start();

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 1.5);

    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start();

    oscillatorRef.current = { stop: () => { osc.stop(); osc2.stop(); } };
    gainRef.current = gain;
    setIsPlaying(true);
    if (!purchaseSubmitted) setFreePlays(prev => prev - 1);
    if (!purchaseSubmitted) setTimeout(() => stopSound(), 15000);
  }, [isPlaying, freePlays, purchaseSubmitted, stopSound]);

  useEffect(() => {
    return () => { if (oscillatorRef.current) { try { oscillatorRef.current.stop(); } catch (e) { /* */ } } };
  }, []);

  const handleFormChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handlePurchase = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/frequency-lab/purchase`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, package: 'Personalized Chakra Soundscape', amount: 44.44 }),
      });
      if (res.ok) { setPurchaseSubmitted(true); setShowPaywall(false); setShowPurchaseForm(false); }
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <div className="relative py-20 px-6 bg-gradient-to-b from-white to-[#faf8f5] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-1/4 w-80 h-80 bg-[#9D4EDD]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-[#FF6B9D]/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FFB800]/5 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#9D4EDD]/30 bg-[#9D4EDD]/5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#9D4EDD] animate-pulse" />
            <span className="text-xs text-[#9D4EDD] tracking-[4px] uppercase font-bold">A World Premiere &middot; Patent Pending</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-[#1e1919] mb-4 leading-tight" data-testid="frequency-lab-title">
            Kholey Frequency Lab<span className="text-[#c9a96e] text-2xl align-super">&trade;</span>
          </h1>
          <p className="text-[#61525a] max-w-2xl mx-auto text-base sm:text-lg leading-relaxed mb-4">
            An A.I. that composes a chakra-tuned soundscape personalized to your energy.
            Trained on solfeggio frequencies, lunar phases, and the sonic signatures of master healers —
            then woven through Kholey's studio engineering.
          </p>
          <p className="text-[#c9a96e] text-sm italic mb-10">
            The result is a soundscape no one else on Earth has ever heard. Yours alone.
          </p>
          <div className="inline-block px-3 py-1 border border-[#e8e4de] rounded-full bg-white/50">
            <span className="text-[10px] text-[#7A7A9A] tracking-wider">KHOLEY FREQUENCY LAB&trade; is a registered trademark of Kholey.com&reg;</span>
          </div>
        </div>
      </div>

      {/* Chakra Stones */}
      <div className="relative py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center font-serif text-2xl text-[#1e1919] mb-2">Tap a Stone to Hear Its Frequency</h2>
          <p className="text-center text-[#7A7A9A] text-sm mb-12">
            {purchaseSubmitted ? 'Unlimited access unlocked. Explore all frequencies.' : `${freePlays} free previews remaining`}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 sm:gap-6">
            {CHAKRAS.map((chakra, i) => {
              const isActive = activeChakra?.name === chakra.name && isPlaying;
              return (
                <button
                  key={chakra.name}
                  onClick={() => { setActiveChakra(chakra); playFrequency(chakra.frequency); }}
                  className="group flex flex-col items-center gap-3 transition-all duration-500"
                  data-testid={`chakra-stone-${chakra.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="relative">
                    {isActive && (
                      <>
                        <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ backgroundColor: chakra.color }} />
                        <div className="absolute -inset-3 rounded-full animate-pulse opacity-30" style={{ backgroundColor: chakra.color, filter: 'blur(12px)' }} />
                      </>
                    )}
                    <div
                      className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center transition-all duration-500 cursor-pointer shadow-lg
                        ${isActive ? 'scale-110' : 'group-hover:scale-105 group-hover:shadow-xl'}`}
                      style={{
                        background: `radial-gradient(circle at 35% 35%, ${chakra.color}ee, ${chakra.color}aa, ${chakra.color}66)`,
                        boxShadow: isActive ? `0 0 40px ${chakra.glow}, 0 8px 30px ${chakra.glow.replace('0.4', '0.15')}` : `0 4px 20px ${chakra.glow.replace('0.4', '0.15')}`,
                      }}
                    >
                      {isActive ? <Volume2 className="w-6 h-6 text-white animate-pulse" /> : <span className="text-white/90 font-serif text-lg">{chakra.note}</span>}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-bold text-[#1e1919]">{chakra.name}</p>
                    <p className="text-[10px] text-[#7A7A9A]">{chakra.frequency} Hz</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active Chakra Detail */}
      {activeChakra && (
        <div className="py-12 px-6">
          <div className="max-w-3xl mx-auto">
            <div
              className="bg-white rounded-3xl p-8 sm:p-10 border shadow-xl transition-all duration-700"
              style={{ borderColor: `${activeChakra.color}30` }}
              data-testid="chakra-detail-panel"
            >
              <div className="flex flex-col sm:flex-row items-start gap-6">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${activeChakra.color}20, ${activeChakra.color}10)` }}>
                  <Music className="w-8 h-8" style={{ color: activeChakra.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="font-serif text-2xl sm:text-3xl text-[#1e1919]">{activeChakra.name} Chakra</h3>
                    <span className="text-xs px-3 py-1 rounded-full border" style={{ color: activeChakra.color, borderColor: `${activeChakra.color}40`, backgroundColor: `${activeChakra.color}10` }}>
                      {activeChakra.sanskrit}
                    </span>
                  </div>
                  <p className="text-[#61525a] mb-4">{activeChakra.description}</p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[
                      { label: 'Frequency', val: `${activeChakra.frequency} Hz`, accent: true },
                      { label: 'Element', val: activeChakra.element },
                      { label: 'Crystal', val: activeChakra.crystal },
                      { label: 'Body Area', val: activeChakra.bodyArea },
                    ].map(d => (
                      <div key={d.label} className="bg-[#faf8f5] rounded-xl p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[#7A7A9A] mb-1">{d.label}</p>
                        <p className={d.accent ? 'font-mono text-lg' : 'text-[#1e1919]'} style={d.accent ? { color: activeChakra.color } : {}}>{d.val}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-[#f8f5ff] rounded-xl p-4 border-l-4" style={{ borderColor: activeChakra.color }}>
                    <p className="text-[10px] uppercase tracking-wider text-[#7A7A9A] mb-1">Affirmation</p>
                    <p className="text-[#4A4A6A] italic">&ldquo;{activeChakra.affirmation}&rdquo;</p>
                  </div>
                </div>
              </div>
              {isPlaying && (
                <div className="mt-6 flex justify-center">
                  <button onClick={stopSound} className="flex items-center gap-2 px-6 py-2 rounded-full bg-[#faf8f5] hover:bg-[#e8e4de] border border-[#e8e4de] transition-all text-sm text-[#4A4A6A]" data-testid="stop-frequency-btn">
                    <VolumeX className="w-4 h-4" /> Stop Frequency
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-md" data-testid="frequency-paywall">
          <div className="relative bg-white border border-[#e8e4de] rounded-3xl p-8 sm:p-10 max-w-lg w-full shadow-2xl">
            <button onClick={() => setShowPaywall(false)} className="absolute top-4 right-4 text-[#7A7A9A] hover:text-[#1e1919]"><X className="w-5 h-5" /></button>
            <div className="text-center mb-6">
              <Lock className="w-10 h-10 text-[#9D4EDD] mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-[#1e1919] mb-2">Unlock Your Personal Soundscape</h3>
              <p className="text-[#7A7A9A] text-sm">
                Your free previews have ended. Unlock unlimited access to all 7 solfeggio frequencies,
                plus receive a personalized chakra-tuned soundscape composed exclusively for your energy signature.
              </p>
            </div>
            <div className="bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8] rounded-2xl p-6 mb-6 border border-[#9D4EDD]/20">
              <div className="text-center">
                <p className="text-xs text-[#9D4EDD] uppercase tracking-wider mb-1">One-Time Investment</p>
                <p className="font-serif text-4xl text-[#c9a96e]">$44.44</p>
                <p className="text-[#7A7A9A] text-xs mt-1">Sacred number alignment pricing</p>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-[#4A4A6A]">
                {['Unlimited solfeggio frequency access', 'Personalized chakra soundscape file', 'Lunar-phase calibrated audio', 'Lifetime access — no subscriptions'].map(t => (
                  <li key={t} className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#c9a96e]" /> {t}</li>
                ))}
              </ul>
            </div>
            {!showPurchaseForm ? (
              <button onClick={() => setShowPurchaseForm(true)} className="w-full py-4 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all" data-testid="unlock-soundscape-btn">
                Unlock My Soundscape
              </button>
            ) : (
              <form onSubmit={handlePurchase} className="space-y-3" data-testid="frequency-purchase-form">
                <input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleFormChange} required className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="freq-fullname" />
                <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleFormChange} required className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="freq-email" />
                <input type="tel" name="phone" placeholder="Phone (optional)" value={formData.phone} onChange={handleFormChange} className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="freq-phone" />
                <p className="text-[10px] text-[#7A7A9A] text-center">Payment processing via Zoho Payments (coming soon).</p>
                <button type="submit" disabled={submitting} className="w-full py-4 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-bold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="freq-submit-btn">
                  {submitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Reserve My Soundscape — $44.44'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Purchase Success Banner */}
      {purchaseSubmitted && (
        <div className="px-6 pb-10">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-[#00C853]/10 to-[#c9a96e]/10 border border-[#00C853]/30 rounded-2xl p-6 text-center" data-testid="purchase-success">
            <Sparkles className="w-6 h-6 text-[#c9a96e] mx-auto mb-2" />
            <h3 className="font-serif text-xl text-[#1e1919] mb-1">Your Soundscape Is Being Composed</h3>
            <p className="text-[#7A7A9A] text-sm">Thank you. We will email your personalized chakra soundscape within 24 hours.</p>
          </div>
        </div>
      )}

      {/* About Solfeggio */}
      <div className="py-16 px-6 bg-gradient-to-b from-[#faf8f5] to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#1e1919] mb-6">The Ancient Solfeggio Scale</h2>
          <p className="text-[#7A7A9A] max-w-2xl mx-auto text-sm leading-relaxed mb-10">
            These sacred frequencies were used in Gregorian chants and ancient hymns. Each tone corresponds
            to a specific chakra and produces measurable vibrational effects on the body's energy field.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Frequencies', val: '7 Solfeggio Tones' },
              { label: 'Range', val: '396 - 963 Hz' },
              { label: 'Tradition', val: '3,000+ Years' },
              { label: 'Energy Centers', val: '7 Chakras' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl p-5 border border-[#e8e4de] shadow-sm">
                <p className="text-xs text-[#7A7A9A] uppercase tracking-wider mb-1">{s.label}</p>
                <p className="font-serif text-lg text-[#9D4EDD]">{s.val}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 text-center border-t border-[#e8e4de]">
        <p className="text-[10px] text-[#7A7A9A]">Kholey Frequency Lab&trade; &middot; Kholey.com&reg; &middot; All Rights Reserved</p>
      </div>
    </div>
  );
};

export default ChakrasPage;
